var kvSync_api = (Vue, config) => {
  let resource = null;
  const actions = {
    getValueByKey: {
      method: 'GET',
      url: `${config.kvSyncApiUrl}/api/aha-sync/kv`,
    },
    getValuesByPath: {
      method: 'GET',
      url: `${config.kvSyncApiUrl}/api/aha-sync/kv/paths`,
    },
    createKeyValue: {
      method: 'POST',
      url: `${config.kvSyncApiUrl}/api/aha-sync/kv`
    }
  };

  const initResource = () => {
    resource = Vue.resource('kv', {}, actions);
  };

  const getValueByKey = (key) => {
    return resource.getValueByKey({ key })
  };

  const createKeyValue = (payload) => {
    return resource.createKeyValue(payload)
  };

  const getValuesByPath = (path) => {
    return resource.getValuesByPath({ path })
  };

  return {
    initResource,
    getValueByKey,
    getValuesByPath,
    createKeyValue,
  }
};

const defaultSubscribeOptions = {
  once: false,
};

const KVSYNC_TOPIC_PREFIX = 'LS1';

class KvSyncSubscriber {
  constructor(mqttClient) {
    this.callbacks = {};
    this.client = mqttClient;
  }

  _isMatch(pattern, topic) {
    const regexPattern = pattern.replace(/#/g, '.*');
    const regex = new RegExp('^' + regexPattern + '$');
    return regex.test(topic)
  }

  _getCallbacks(key) {
    if (!key) {
      return []
    }
    if (!this.callbacks[key]) {
      const matchingCallbackKeys =
        Object.keys(this.callbacks).filter((callbackPattern) => this._isMatch(callbackPattern, key));
      if (matchingCallbackKeys.length > 0) {
        return this.callbacks[matchingCallbackKeys[0]]
      }
      return []
    }
    return this.callbacks[key]
  }

  _addCallback(key, callback) {
    if (!key) {
      throw new Error(`Can not add callback for following key: ${key}`)
    }
    if (!this.callbacks[key]) {
      this.callbacks[key] = [];
    }

    this.callbacks[key].push(callback);
  }

  _setCallback(key, callback) {
    if (!key) {
      throw new Error(`Can not set callback for following key: ${key}`)
    }
    this.callbacks[key] = [callback];
  }

  _removeCallbacks(key, callback) {
    const callbacks = this._getCallbacks(key);
    if (callback) {
      const idx = callbacks.indexOf(callback);
      if (idx > -1) {
        callbacks.splice(idx, 1);
      }
    } else {
      delete this.callbacks[key];
    }
  }

  extractTopicPrefix(topic) {
    const topicParts = topic.split('/');
    if (topicParts[1] === KVSYNC_TOPIC_PREFIX) {
      return topicParts.slice(2).join('/')
    }
    return topic
  }

  init() {
    this.client.on('message', (topic, message, packet) => {
      const callbacks = this._getCallbacks(topic);
      const topicWithoutPrefix = this.extractTopicPrefix(topic);
      callbacks.forEach((callback) => {
        callback(topicWithoutPrefix, message.toString(), packet);
      });
    });
  }

  subscribeKey(key, callback, options = defaultSubscribeOptions) {
    if (options.once) {
      this._setCallback(key, callback);
    } else {
      this._addCallback(key, callback);
    }
    this.client.subscribe(key);
  }

  unsubscribeKey(uri, callback) {
    this._removeCallbacks(uri, callback);
    this.client.unsubscribe(uri);
  }
}

const _state = {};

const actions = (kvSyncApi, kvSyncMqttSubscriber) => ({
  fetchValueByKeyAction: async ({ dispatch }, payload) => {
    if (!payload.key) return
    const value = await kvSyncApi.getValueByKey(payload.key);
    dispatch('updateKeyValueAction', {
      key: payload.key,
      value,
    });
  },
  subscribeKeyAction: async ({ dispatch }, payload) => {
    dispatch('fetchValueByKeyAction', payload.key);
    kvSyncMqttSubscriber.subscribeKey(payload.key, (topic, message) => {
      dispatch('updateKeyValueAction', {
        key: topic,
        value: message,
      });
      if (payload.callback) {
        payload.callback(topic, message);
      }
    }, payload.options);
  },

  updateKeyValueAction: () => { }, // only declare the action, plugins will listen to this to update state accordingly

  unsubscribeKeyAction(_, payload) {
    kvSyncMqttSubscriber.unsubscribeKey(payload);
  },
});

var kvSync_module = (kvSyncApi, kvSyncMqttSubscriber) => ({
  namespaced: true,
  state: _state,
  actions: actions(kvSyncApi, kvSyncMqttSubscriber),
});

const SLIDE_TYPES = {
  WORD_CLOUD: 'WORD_CLOUD',
  POLL: 'POLL',
};
const MESSAGE_DELIMITER = '*|*';

const STATS_TYPES = {
  ANSWERER_COUNT: 'ANSWERER_COUNT',
  ANSWER_COUNT: 'ANSWER_COUNT',
  AVERAGE_SCORE: 'AVERAGE_SCORE',
  CORRECT_ANSWER_COUNT: 'CORRECT_ANSWER_COUNT',
  INCORRECT_ANSWER_COUNT: 'INCORRECT_ANSWER_COUNT',
  REACTION_COUNT: 'REACTION_COUNT',
  LAST_REACTION: 'LAST_REACTION',
  AVERAGE_TEAM_SCORE: 'AVERAGE_TEAM_SCORE',
  TOTAL_TEAM_SCORE: 'TOTAL_TEAM_SCORE',
  FASTEST_TEAM_SCORE: 'FASTEST_TEAM_SCORE',
};

const STATS_TYPES_INDEX = [
  STATS_TYPES.ANSWER_COUNT,
  STATS_TYPES.ANSWERER_COUNT,
  STATS_TYPES.AVERAGE_SCORE,
  STATS_TYPES.CORRECT_ANSWER_COUNT,
  STATS_TYPES.INCORRECT_ANSWER_COUNT,
];

const AGGREGATION_LEVELS = {
  PER_ACTIVITY: 'PER_ACTIVITY',
  PER_QUESTION: 'PER_QUESTION',
  PER_ANSWER_VALUE: 'PER_ANSWER_VALUE',
  PER_LIVE_SESSION: 'PER_LIVE_SESSION',
  PER_REACTION_TYPE: 'PER_REACTION_TYPE',
};

const AGGREGATION_LEVELS_INDEX = [
  AGGREGATION_LEVELS.PER_ANSWER_VALUE,
  AGGREGATION_LEVELS.PER_QUESTION,
  AGGREGATION_LEVELS.PER_ACTIVITY,
  AGGREGATION_LEVELS.PER_LIVE_SESSION,
];

const LIVE_STATS_KV_SYNC_TOPIC_PREFIX = 'ST1';
const KV_LIVE_STATS_UNLEASH_FLAG = 'liveStatsV2/canUseKvSyncLiveStats';

const prefixTopic = (prefix, topic) => {
  return `${prefix}${topic}`
};

const statsGetter = {
  [`${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      answerCount: Number(message),
    }
  },
  [`${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: arr[3],
      answerCount: Number(message),
    }
  },
  [`${STATS_TYPES.ANSWERER_COUNT}_${AGGREGATION_LEVELS.PER_QUESTION}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answererCount: Number(message),
    }
  },
  [`${STATS_TYPES.AVERAGE_SCORE}_${AGGREGATION_LEVELS.PER_QUESTION}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.AVERAGE_SCORE}_${AGGREGATION_LEVELS.PER_QUESTION}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: Number(arr[2]),
      averageScore: message,
    }
  },
  [`${STATS_TYPES.AVERAGE_SCORE}_${AGGREGATION_LEVELS.PER_ACTIVITY}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.AVERAGE_SCORE}_${AGGREGATION_LEVELS.PER_ACTIVITY}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      averageScore: message,
    }
  },
  [`${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: arr[3],
      answerCount: message,
    }
  },
  [`${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: arr[3],
      answerCount: message,
    }
  },
  [`${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      answerCount: message,
    }
  },
  [`${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      answerCount: message,
    }
  },
  [`${STATS_TYPES.REACTION_COUNT}_${AGGREGATION_LEVELS.PER_REACTION_TYPE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.REACTION_COUNT}_${AGGREGATION_LEVELS.PER_REACTION_TYPE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      reactionType: arr[2],
      reactionCount: Number(message),
    }
  },
  [`${STATS_TYPES.LAST_REACTION}_${AGGREGATION_LEVELS.PER_LIVE_SESSION}`]: (arr, message) => {
    const reactionInfo = message.split(MESSAGE_DELIMITER);
    return {
      type: `${STATS_TYPES.LAST_REACTION}_${AGGREGATION_LEVELS.PER_LIVE_SESSION}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      reactionType: reactionInfo[0],
      audienceName: reactionInfo[1],
    }
  },

};

const statsGettersV2 = {
  ...statsGetter,
  [`${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: decodeURIComponent(arr[3]),
      answerCount: Number(message),
    }
  },
  [`${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: decodeURIComponent(arr[3]),
      answerCount: message,
    }
  },
  [`${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: decodeURIComponent(arr[3]),
      answerCount: message,
    }
  },
};

class LiveStats {
  constructor(mqttClient, store, config) {
    this.callbacks = {};
    this.mqttClient = mqttClient;
    this.$store = store;
    this.config = config;
  }

  configStore(store) {
    if (store) {
      this.$store = store;
    }
  }

  isUseKvSync() {
    return this.$store.getters[KV_LIVE_STATS_UNLEASH_FLAG]
  }

  static getCallbackKey({ statsType, aggregationLevel, identifier }) {
    return `${statsType}_${aggregationLevel}_${identifier}`
  }

  _getCallbacks({ identifier, statsType, aggregationLevel }) {
    if (!identifier || !statsType || !aggregationLevel) return []
    const key = LiveStats.getCallbackKey({ identifier, statsType, aggregationLevel });
    if (!this.callbacks[key]) {
      return []
    }
    return this.callbacks[key]
  }

  getStatParser(statsType, aggregationLevel) {
    if (this.isUseKvSync()) {
      return statsGettersV2[`${statsType}_${aggregationLevel}`]
    }
    return statsGetter[`${statsType}_${aggregationLevel}`]
  }

  init(store) {
    this.mqttClient.on('message', (topic, message) => {
      let arr = topic.split('/');

      // trim the v2 prefix if it exists
      if (arr[1] === LIVE_STATS_KV_SYNC_TOPIC_PREFIX) {
        arr = arr.slice(1);
      }

      const statsType = arr[2];
      const aggregationLevel = arr[3];
      const topicWithoutPrefix = arr.slice(2).join('/');
      const statParser = this.getStatParser(statsType, aggregationLevel);
      if (typeof statParser != 'function') {
        return
      }
      const statsUpdate = statParser(arr.slice(4), message.toString());

      const callbacks = this._getCallbacks({
        statsType,
        aggregationLevel,
        identifier: statsUpdate.identifier,
      });
      callbacks.forEach((callback) => {
        callback(topicWithoutPrefix, message.toString(), statsUpdate);
      });
    });
  }

  _addCallback({ statsType, aggregationLevel, identifier }, callback) {
    if (!identifier || !statsType) {
      return
    }
    const key = LiveStats.getCallbackKey({ statsType, aggregationLevel, identifier });
    if (!this.callbacks[key]) {
      this.callbacks[key] = [];
    }

    this.callbacks[key].push(callback);
  }

  _removeCallbacks({ statsType, aggregationLevel, identifier }, callback) {
    if (!identifier || !statsType) {
      return
    }
    const callbacks = this._getCallbacks({ statsType, aggregationLevel, identifier });
    if (callback) {
      const idx = callbacks.indexOf(callback);
      if (idx > -1) {
        callbacks.splice(idx, 1);
      }
    } else {
      delete this.callbacks[LiveStats.getCallbackKey({ statsType, aggregationLevel, identifier })];
    }
  }

  subscribeStats({
    statsType, aggregationLevel, identifier,
  }, callback) {
    let topic = `/${this.config.livestatsTopicPrefix}/${statsType}/${aggregationLevel}/${identifier}/#`;
    if (this.isUseKvSync()) {
      topic = `/${LIVE_STATS_KV_SYNC_TOPIC_PREFIX}${topic}`;
    }
    this._addCallback({ statsType, aggregationLevel, identifier }, callback);
    this.mqttClient.subscribe(topic);
  }

  unsubscribeStats({
    identifier, statsType, aggregationLevel,
  }, callback) {
    let topic = `/${this.config.livestatsTopicPrefix}/${statsType}/${aggregationLevel}/${identifier}/#`;
    if (this.isUseKvSync()) {
      topic = `/${LIVE_STATS_KV_SYNC_TOPIC_PREFIX}${topic}`;
    }
    this._removeCallbacks({ statsType, aggregationLevel, identifier }, callback);
    this.mqttClient.unsubscribe(topic);
  }
}

const LiveStatsTopics = {
  answerCountPerAnswerValueTopic(activityId, questionId, version, answerValue) {
    return `${STATS_TYPES.ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${activityId}/${version}/${questionId}/${answerValue}`
  },

  answerCountPerActivityTopic(activityId, version) {
    return `${STATS_TYPES.ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${activityId}/${version}`
  },

  averageScorePerQuestionTopic(activityId, version, questionId) {
    return `${STATS_TYPES.AVERAGE_SCORE}/${AGGREGATION_LEVELS.PER_QUESTION}/${activityId}/${version}/${questionId}`
  },

  averageScorePerActivityTopic(activityId, version) {
    return `${STATS_TYPES.AVERAGE_SCORE}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${activityId}/${version}`
  },

  answererCountPerQuestionTopic(activityId, questionId, version) {
    return `${STATS_TYPES.ANSWERER_COUNT}/${AGGREGATION_LEVELS.PER_QUESTION}/${activityId}/${version}/${questionId}`
  },

  reactionCountPerTypeTopic(liveSessionId, version, reactionType) {
    return `${STATS_TYPES.REACTION_COUNT}/${AGGREGATION_LEVELS.PER_REACTION_TYPE}/${liveSessionId}/${version}/${reactionType}`
  },

  correctAnswerCountPerAnswerValueTopic(activityId, version, questionId, answerValue) {
    return `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${activityId}/${version}/${questionId}/${answerValue}`
  },

  incorrectAnswerCountPerAnswerValueTopic(activityId, version, questionId, answerValue) {
    return `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${activityId}/${version}/${questionId}/${answerValue}`
  },

  correctAnswerCountPerActivityTopic(activityId, version) {
    return `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${activityId}/${version}`
  },

  incorrectAnswerCountPerActivityTopic(activityId, version) {
    return `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${activityId}/${version}`
  },
};

const MUTATION_TYPES = {
  UPDATE_STATS: 'updateStats',
};

const mutations = (Vue) => ({
  updateStats(state, statsUpdate) {
    Vue.set(state, statsUpdate.topic, statsUpdate.message);
  },
});

const fetchStatsByKeyActionBuilder = (kvSyncApi, config) => {
  return async ({ commit, rootGetters }, { statsKeys }) => {
    if (!rootGetters[KV_LIVE_STATS_UNLEASH_FLAG]) return
    if (!statsKeys || statsKeys.length == 0) return
    const statsRequests = statsKeys.map((statsKey) => {
      return kvSyncApi.getValueByKey(prefixTopic(`/${LIVE_STATS_KV_SYNC_TOPIC_PREFIX}/${config.livestatsTopicPrefix}/`, statsKey))
        .then((response) => response.body)
    });
    Promise.allSettled(statsRequests).then((results) => {
      return results.forEach((result, index) => {
        if (result.status != 'fulfilled' || !result.value) return
        const { key, value } = result.value;
        if (!key || value == null) return
        commit(MUTATION_TYPES.UPDATE_STATS, { topic: statsKeys[index], message: value });
      })
    }).catch((error) => {
      throw error
    });
  }
};

const fetchStatsByWildCardActionBuilder = (kvSyncApi, config) => {
  return async ({ commit, rootGetters }, { path }) => {
    if (!path) return
    const resp = await kvSyncApi.getValuesByPath(prefixTopic(`/${LIVE_STATS_KV_SYNC_TOPIC_PREFIX}/${config.livestatsTopicPrefix}/`, path));
    const keyValuePairs = resp.body && resp.body.keysvalues;
    if (!keyValuePairs || keyValuePairs.length == 0) return
    keyValuePairs.forEach(({ key, value }) => {
      const topicWithoutPrefix = key.replace(`/${LIVE_STATS_KV_SYNC_TOPIC_PREFIX}/${config.livestatsTopicPrefix}/`, '');
      commit(MUTATION_TYPES.UPDATE_STATS, { topic: decodeURIComponent(topicWithoutPrefix), message: value });
    });

  }
};

const fetchStatsFromLiveApiActionBuilder = (LiveApi) => {
  return async ({ commit }, payload) => {
    const stats = await LiveApi.getStats({
      slideId: payload.id,
      version: payload.version,
    });
    if (!stats) return

    stats.perActitityStats.forEach((s) => {
      const topic = `${STATS_TYPES_INDEX[s.statsType]}/${AGGREGATION_LEVELS_INDEX[s.aggregationLevel]}/${s.activityId}/${s.activityVersion}`;
      const message = s.count;
      commit(MUTATION_TYPES.UPDATE_STATS, { topic, message });
    });

    stats.perAnswerValueStats.forEach((s) => {
      const topic = `${STATS_TYPES_INDEX[s.statsType]}/${AGGREGATION_LEVELS_INDEX[s.aggregationLevel]}/${s.activityId}/${s.activityVersion}/${s.questionId}/${s.answerValue}`;
      const message = s.count;
      commit(MUTATION_TYPES.UPDATE_STATS, { topic, message });
    });
  }
};

const subscribeStatsActionBuilder = (liveStats) => {
  return async (store, payload) => {
    liveStats.subscribeStats(payload, (topic, message, statsUpdate) => {
      store.commit(MUTATION_TYPES.UPDATE_STATS, {
        topic: decodeURIComponent(topic),
        message,
      });
      if (payload.callback) {
        payload.callback(statsUpdate);
      }
    });
  }
};


const unsubscribeStatsActionBuilder = (liveStats) => {
  return (_, payload) => {
    liveStats.unsubscribeStats(payload);
  }
};

const getters = {
  answerCountPerAnswerValue(state) {
    return (activityId, questionId, version, answerValue) => {
      return Number(state[LiveStatsTopics.answerCountPerAnswerValueTopic(activityId, questionId, version, answerValue)] || 0)
    }
  },
  answerCountPerActivity(state) {
    return (activityId, version) => {
      return Number(state[LiveStatsTopics.answerCountPerActivityTopic(activityId, version)] || 0)
    }
  },
  answererCountPerQuestion(state) {
    return (activityId, questionId, version) => {
      return Number(state[LiveStatsTopics.answererCountPerQuestionTopic(activityId, questionId, version)] || 0)
    }
  },
  averageScorePerQuestion(state) {
    return (activityId, version, questionId) => {
      return state[LiveStatsTopics.averageScorePerQuestionTopic(activityId, version, questionId)]
    }
  },
  averageScorePerActivity(state) {
    return (activityId, version) => {
      return state[LiveStatsTopics.averageScorePerActivityTopic(activityId, version)]
    }
  },
  reactionCountPerType(state) {
    return (liveSessionId, version, reactionType) => {
      return Number(state[LiveStatsTopics.reactionCountPerTypeTopic(liveSessionId, version, reactionType)] || 0)
    }
  },
  correctAnswerCountPerActivity(state) {
    return (activityId, version) => {
      return Number(state[LiveStatsTopics.correctAnswerCountPerActivityTopic(activityId, version)] || 0)
    }
  },
  incorrectAnswerCountPerActivity(state) {
    return (activityId, version) => {
      return Number(state[LiveStatsTopics.incorrectAnswerCountPerActivityTopic(activityId, version)] || 0)
    }
  },
  correctAnswerCountPerAnswerValue(state) {
    return (activityId, version, questionId, answerValue) => {
      return Number(state[LiveStatsTopics.correctAnswerCountPerAnswerValueTopic(activityId, version, questionId, answerValue)] || 0)
    }
  },
  incorrectAnswerCountPerAnswerValue(state) {
    return (activityId, version, questionId, answerValue) => {
      return Number(state[LiveStatsTopics.incorrectAnswerCountPerAnswerValueTopic(activityId, version, questionId, answerValue)] || 0)
    }
  },
};

const liveStatsDefaultState = {};

export { AGGREGATION_LEVELS, AGGREGATION_LEVELS_INDEX, KV_LIVE_STATS_UNLEASH_FLAG, kvSync_api as KvSyncApi, KvSyncSubscriber as KvSyncMqttSubscriber, kvSync_module as KvSyncVuexModule, LIVE_STATS_KV_SYNC_TOPIC_PREFIX, LiveStats as LiveStatsMqttSubscriber, LiveStatsTopics, MESSAGE_DELIMITER, SLIDE_TYPES, STATS_TYPES, STATS_TYPES_INDEX, fetchStatsByKeyActionBuilder, fetchStatsByWildCardActionBuilder, fetchStatsFromLiveApiActionBuilder, liveStatsDefaultState, getters as liveStatsVuexGetters, mutations as liveStatsVuexMutations, prefixTopic, subscribeStatsActionBuilder, unsubscribeStatsActionBuilder };
